<template>
    <v-container fluid>
        <B-confirm-edit-dialog
			ref="dialog"
			@on-consent="submit"
		></B-confirm-edit-dialog>
        <v-form ref="form" @submit.prevent :disabled="loading || submitLoading">
            <h1 class="text-body-1 mb-2">{{$T('training-details')}}</h1>
            <v-row dense class="mb-2">
                <!-- course-name -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <v-text-field
                        v-model="courseName"
                        :label="$T('inputs.course-name')"
                        outlined
                        dense
                        hide-details
                        :rules="[rules.required]"
                    />
                </v-col>
                
                <!-- dates -->
                <v-col lg="3" md="3" sm="4" cols="6">
                    <v-row dense>
                        <!-- start-date -->
                        <v-col cols="6">
                            <bee-date-picker
                                v-model="startDate"
                                :input-label="$T('inputs.start-date')"
                                menu-picker
                                :input-props="{
                                    readonly: true,
                                    outlined: true,
                                    dense: true,
                                    prependInnerIcon: '$calendar'
                                }"
                                hide-details
                                :years-over-max="1"
                                :rules="[rules.required]"
                                @change="updateDuration"
                            />
                        </v-col>
                        
                        <!-- end-date -->
                        <v-col cols="6">
                            <bee-date-picker
                                v-model="endDate"
                                :input-label="$T('inputs.end-date')"
                                menu-picker
                                :input-props="{
                                    readonly: true,
                                    outlined: true,
                                    dense: true,
                                    prependInnerIcon: '$calendar'
                                }"
                                hide-details
                                :years-over-max="1"
                                :rules="[rules.required]"
                                @change="updateDuration"
                            />
                        </v-col>
                    </v-row>
                </v-col>
                
                <!-- duration -->
                <v-col lg="1" md="3" sm="4" cols="6">
                    <v-text-field
                        v-model="duration"
                        :label="$T('inputs.duration')"
                        outlined
                        dense
                        hide-details
                        :rules="[rules.required]"
                    />
                </v-col>
            
                <!-- couch -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <v-combobox
                        v-model="coachId"
                        :label="$T('inputs.couch')"
                        :items="employees"
                        item-text="arabicFullName"
                        item-value="arabicFullName"
                        outlined
                        dense
                        hide-details
                        :rules="[rules.required]"
                        :return-object="false"
                    />
                </v-col>
                
                <!-- course-place -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <v-text-field
                        v-model="coursePlace"
                        :label="$T('inputs.course-place')"
                        outlined
                        dense
                        hide-details
                    />
                </v-col>
                
                <!-- course-presenter -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <v-text-field
                        v-model="coursePresenter"
                        :label="$T('inputs.course-presenter')"
                        outlined
                        dense
                        hide-details
                    />
                </v-col>
            </v-row>
        </v-form>

        <!-- tabs -->
        <v-tabs
        	v-model="tab"
        	@change="$nextTick(() => tab <= 2 ? (() => {
                $refs.table.resetInputs();
                $refs.table.resetValidation();
            })() : null)"
        >
            <v-tab>{{$T('tabs.training-requirements')}}</v-tab>
            <v-tab>{{$T('tabs.training-plan')}}</v-tab>
            <v-tab>{{$T('tabs.trainees')}}</v-tab>
            <v-tab>{{$T('tabs.training-rate')}}</v-tab>
        </v-tabs>
        <v-divider class="mb-2"/>
        
        <!-- table -->
        <bee-handy-smart-table
            v-if="tab <= 2"
            v-model="tableInputs"
            ref="table"
        	:headers="headers"
        	:items="items"
            :loading="loading"
            :show-add-row="!loading && !submitLoading"
            :editable-rows="!loading && !submitLoading"
            :items-per-page="-1"
            dense
            zebra
            :data-table-props="{ disableSort: true }"
            :shown-rows-text="$T('shown-rows-text')"
            hide-no-data-area
            hide-inputs-details
            hide-shown-rows-text
            hide-default-footer
            @keydown.enter="submitEntry"
        >

            <!-- actions -->
            <template v-slot:item.actions="{ item, index }">
                <div class="d-flex align-center">
                    <v-btn
                        icon
                        small
                        color="edit-color"
                        @click="editRowIndex = index; $refs.table.activeEditForRow(index);"
                    >
                        <v-icon size="22">$edit</v-icon>
                    </v-btn>
                    <v-btn
                        icon
                        small
                        color="alert"
                        @click="deleteEntry(item.id)"
                    >
                        <v-icon size="22">$delete</v-icon>
                    </v-btn>
                </div>
            </template>
            
            <!-- traineeId -->
            <template v-slot:item.traineeId="{ item }">
                {{getEmployeeById(item.traineeId).arabicFullName}}
            </template>

            <template v-slot:input.traineeId="{ on, attr }">
                <v-autocomplete
                    :items="employees.filter(c =>
                        c.employeeId !== coachId &&
                        c.jobDescriptionId &&
                        !items.map(e => e.traineeId).includes(c.employeeId)
                    )"
                    item-text="arabicFullName"
                    item-value="employeeId"
                    v-on="on"
                    v-bind="attr"
                />
            </template>

            <template v-slot:no-input.actions>
                <v-btn icon small color="addRow" @click="submitEntry">
                    <v-icon size="22">mdi-check-circle-outline</v-icon>
                </v-btn>
            </template>
        </bee-handy-smart-table>

        <!-- rating -->
        <v-simple-table
        	v-if="tab === 3"
            dense
            class="elevation-2"
        >
            <thead>
                <tr>
                    <th class="text-start">{{$T('headers.trainings.subject')}}</th>
                    <th v-for="item in evaluationsTypes" :key="item.name" :style="{ width: `${160}px` }">{{ item.name }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(entry, i) in evaluationsEntires" :key="i">
                    <td>{{entry.name}}</td>
                    <td v-for="(item, j) in evaluationsTypes" :key="j">
                        <v-checkbox
                        	v-model="entry.value"
                            :ripple="false"
                            dense
                        	:true-value="item.id"
                        	:false-value="null"
                            on-icon="$radioOn"
                            off-icon="$radioOff"
                            :disabled="loading || submitLoading"
                        	hide-details
                        	class="mt-0"
                        />
                    </td>
                </tr>
            </tbody>
        </v-simple-table>

        <v-footer absolute color="transparent" height="50" class="justify-end align-start">
            <v-btn
            	width="160"
            	color="success white--text"
            	class="me-4"
                :loading="submitLoading || loading"
            	@click="$refs.dialog.activeConfirmDialog()"
            >
                {{$T('save')}}
            </v-btn>
            <v-btn width="80" :to="{ name: 'trainings' }">
                {{$T('back')}}
            </v-btn>
        </v-footer>
    </v-container>
</template>

<script>
import rules from '@/utils/validation rules';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import { evaluationsTypes } from '@/utils/enum'
import BConfirmEditDialog from '@/components/forms/BConfirmEditDialog'
 
export default {
    name: 'Training',
    components: { BConfirmEditDialog },
    data() {
        return {
            rules,

            tab: 0,
            editRowIndex: null,

            loading: false,
            submitLoading: false,

            evaluationsTypes,

            evaluationsEntires: [],
            // { name: 'ارتباط موضوع الدورة بعمل الشركة', value: null },
            // { name: 'توقيت المحاضرات', value: null },
            // { name: 'الأساليب التدريبية المتبعة', value: null },
            // { name: 'مستوى المدرب بنقل المعلومات الأساسية للمتدربين', value: null },
            // { name: 'مستوى جودة الوسائل الإيضاحية', value: null },
            // { name: 'المادة التدريبية متوفرة ومناسبة', value: null }

            // data
            tableInputs: {},

            courseName: null,
            startDate: null,
            endDate: null,
            duration: null,
            coachId: null,
            coursePlace: null,
            coursePresenter: null,

            requirements: [],
            plans: [],
            trainees: [],
        }
    },

    computed: {
        ...mapState({
            employees: state => state.employee.employees,
            positions: (state) => state.positions.positions,
            trainingEvaluationTypes: state => state.trainings.trainingEvaluationTypes,
        }),
        ...mapGetters({
            getEmployeeById: 'employee/getEmployeeById',
            getPositionById: 'positions/getPositionById'
        }),

        headers() {
            let headers = [];
            switch (this.tab) {
            case 0: headers = this.requirementsHeaders; break;
            case 1: headers = this.planHeaders; break;
            case 2: headers = this.traineesHeaders; break;
            }
            for (let i = 0; i < headers.length; i++) {
                headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-2' : 'px-2';
            }
            return [
                ...headers,
                {
                    text: '',
                    name: 'actions',
                    value: 'actions',
                    width: 80,
                    noInput: true,
                    columnClass: 'px-2'
                }
            ];
        },
        requirementsHeaders() {
            const headers = [
                {
                    text: this.$T('headers.trainings.requirement'),
                    name: 'requirement',
                    value: 'requirement',
                    inputRules: [rules.required]
                },
                {
                    text: this.$T('headers.trainings.reason'),
                    name: 'reason',
                    value: 'reason',
                    width: '30%',
                    inputRules: [rules.required]
                },
                {
                    text: this.$T('headers.trainings.responsible-about-checking'),
                    name: 'responsibleAboutChecking',
                    value: 'responsibleAboutChecking',
                    width: '30%',
                    inputRules: [rules.required]
                },
            ];
            return headers;
        },
        planHeaders() {
            const headers = [
                {
                    text: this.$T('headers.trainings.subject'),
                    name: 'subject',
                    value: 'subject',
                    inputRules: [rules.required]
                },
                {
                    text: this.$T('headers.trainings.sessions-count'),
                    name: 'sessionsCount',
                    value: 'sessionsCount',
                    width: '30%',
                    type: 'number',
                    inputRules: [rules.isNumber]
                },
                {
                    text: this.$T('headers.notes'),
                    name: 'notes',
                    value: 'notes',
                    width: '30%',
                },
            ]
            return headers;
        },
        traineesHeaders() {
            const headers = [
                {
                    text: this.$T('headers.trainings.trainee'),
                    name: 'traineeId',
                    value: 'traineeId',
                    inputRules: [rules.required]
                },
                {
                    text: this.$T('headers.position-name'),
                    name: 'jobPosition',
                    value: 'jobPosition',
                    width: '30%',
                    noInput: true,
                },
                {
                    text: this.$T('headers.trainings.rate'),
                    name: 'rate',
                    value: 'rate',
                    width: '30%',
                    noInput: true,
                },
            ]
            return headers;
        },
        
        items() {
            let items = [];
            switch (this.tab) {
            case 0: items = this.requirements; break;
            case 1: items = this.plans; break;
            case 2: items = this.trainees; break;
            }
            return items
        }
    },

    methods: {
        updateDuration() {
            if (this.startDate && this.endDate) {
                this.duration =
                    moment(this.endDate).diff(this.startDate, 'days') + ' ' + this.$T('days');
            }
        },

        submitEntry() {
            if (this.$refs.table.validate()) {
                switch (this.tab) {
                // requirements
                case 0: {
                    if (this.editRowIndex === null) {
                        this.requirements.push({
                            id: this.requirements.length + 1,
                            requirement: this.tableInputs.requirement,
                            reason: this.tableInputs.reason,
                            responsibleAboutChecking: this.tableInputs.responsibleAboutChecking,
                        })
                    } else {
                        this.requirements[this.editRowIndex].requirement = this.tableInputs.requirement;
                        this.requirements[this.editRowIndex].reason = this.tableInputs.reason;
                        this.requirements[this.editRowIndex].responsibleAboutChecking = this.tableInputs.responsibleAboutChecking;
                    }
                    break;
                }
                // plans
                case 1: {
                    if (this.editRowIndex === null) {
                        this.plans.push({
                            id: this.plans.length + 1,
                            subject: this.tableInputs.subject,
                            sessionsCount: this.tableInputs.sessionsCount,
                            notes: this.tableInputs.notes,
                        })
                    } else {
                        this.plans[this.editRowIndex].subject = this.tableInputs.subject;
                        this.plans[this.editRowIndex].sessionsCount = this.tableInputs.sessionsCount;
                        this.plans[this.editRowIndex].notes = this.tableInputs.notes;
                    }
                    break;
                }
                // trainees
                case 2: {
                    let jobPosition;
                    const jobDescriptionId =
                        this.getEmployeeById(this.tableInputs.traineeId).jobDescriptionId;
                    if (jobDescriptionId) jobPosition = this.getPositionById(jobDescriptionId)?.name;

                    if (this.editRowIndex === null) {
                        this.trainees.push({
                            id: this.trainees.length + 1,
                            traineeId: this.tableInputs.traineeId,
                            jobPosition
                        })
                    } else {
                        this.trainees[this.editRowIndex].traineeId = this.tableInputs.traineeId;
                        this.trainees[this.editRowIndex].jobPosition = jobPosition;
                    }
                    break;
                }
                }

                if (this.editRowIndex !== null) {
                    this.editRowIndex = null;
                    this.$refs.table.resetEdit();
                }
                this.$nextTick(() => {
                    this.$refs.table.resetInputs();
                    this.$refs.table.resetInputsFocus();
                })
            }
        },

        deleteEntry(id) {
            let targetArray = '';
            switch (this.tab) {
            // requirements
            case 0: { targetArray = 'requirements'; break;}
            // plans
            case 1: { targetArray = 'plans'; break;}
            // trainees
            case 2: { targetArray = 'trainees'; break;}
            }

            if (targetArray) {
                const index = this[targetArray].findIndex(c => c.id === id);
                if (index !== -1) {
                    this[targetArray].splice(index, 1);
                    this[targetArray].forEach((c, i) =>
                        this[targetArray][i].id > i ? this[targetArray][i].id-- : null
                    )
                }
            }
        },

        submit() {
            if (this.$refs.form.validate()) {
                this.$refs.dialog.deactivateConfirmDialog()
                this.submitLoading = true;
                const actionName = !this.$route.params.id ? 'create' : 'update';
                this.$store.dispatch(`trainings/${actionName}`, {
                    id: this.$route.params.id,
                    name: this.courseName,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    coachName: this.coachId,
                    place: this.coursePlace,
                    duration: this.duration,
                    organizers: this.coursePresenter,
                    trainees: this.trainees.map(c => c.traineeId),
                    trainingNeed: this.requirements.map(c => ({
                        name: c.requirement,
                        reason: c.reason,
                        verifyNeed: c.responsibleAboutChecking,
                    })),
                    plans: this.plans.map(c => ({
                        title: c.subject,
                        numOfSession: c.sessionsCount,
                        notes: c.notes,
                    })),
                    evaluation: this.evaluationsEntires.map(c => ({
                        evaluationTypeId: c.id,
                        value: c.value
                    }))
                }).then(() => {
                    this.$router.push({ name: 'trainings' });
                }).catch((err) => {
					err.default && err.default(
						!this.$route.params.id ? this.$T('messages.add-fail') : this.$T('messages.edit-fail'),
						false
					);
				}).finally(() => {
                    this.submitLoading = false;
                })
            }
        }
    },

    async created() {
        this.loading = true;
        
        await Promise.all([
            !this.trainingEvaluationTypes.length
                ? this.$store.dispatch('trainings/fetchTrainingEvaluationTypes')
                : null
        ]).then(() => {
            this.trainingEvaluationTypes.forEach(c => {
                this.evaluationsEntires.push({ id: c.id, name: c.type, value: null })
            })
        });
        if (this.$route.params.id) {
            await this.$store.dispatch('trainings/fetchById', { id: this.$route.params.id }).then((data) => {
                this.courseName = data.name;
                this.startDate = moment(data.startDate).format('YYYY-MM-DD');
                this.endDate = moment(data.endDate).format('YYYY-MM-DD');
                this.duration = data.duration;
                this.coachId = data.coachId;
                this.coursePlace = data.place;
                this.coursePresenter = data.organizers;
                this.requirements = data.trainingNeed.map((c, index)  => ({
                    id: index + 1,
                    requirement: c.name,
                    reason: c.reason,
                    responsibleAboutChecking: c.verifyNeed,
                }))
                this.plans = data.trainingPlans.map((c, index) => ({
                    id: index + 1,
                    subject: c.title,
                    sessionsCount: c.numOfSession,
                    notes: c.notes,
                }));
                this.trainees = data.traineesEvaluations.map((c, index) => {
                    let jobPosition;
                    if (c.jobDescriptionId) jobPosition = this.getPositionById(c.jobDescriptionId)?.name;

                    return {
                        id: index + 1,
                        traineeId: c.traineeId,
                        jobPosition
                    }
                });
                this.evaluationsEntires.forEach((c, i) => {
                    this.evaluationsEntires[i].value =
                        data.trainingEvaluations.find(e => e.evaluationTypeId === c.id)?.value ?? null;
                })
            })
        }
        this.loading = false;
    }
}
</script>

<style>

</style>